/**
 * @module features/devices/types/telemetry
 * @description Zod schemas + TypeScript contracts for device security credentials,
 * embedded telemetry documentation and the live (max 30 logs) telemetry dashboard.
 * Mirrors specs/005-device-telemetry-security/contracts/ui-contracts.ts.
 */
import { z } from "zod";

export const RETENTION_LIMIT = 30;

export const WifiStrengthSchema = z.enum(["low", "medium", "high"]);
export type WifiStrength = z.infer<typeof WifiStrengthSchema>;

export const TelemetryDocumentationSchema = z.object({
  ingestion_endpoint: z.string(),
  method: z.string(),
  required_headers: z.record(z.string(), z.string()),
  canonical_signing_algorithm: z.string(),
  response_verification_algorithm: z.string(),
  retention_policy: z.string(),
  sample_payload: z.record(z.string(), z.unknown()),
});
export type TelemetryDocumentation = z.infer<typeof TelemetryDocumentationSchema>;

export const RotationHistoryEntrySchema = z.object({
  id: z.string(),
  previous_secret_prefix: z.string(),
  new_secret_prefix: z.string(),
  reason: z.string(),
  rotated_by_name: z.string().nullable(),
  rotated_at: z.string(),
});
export type RotationHistoryEntry = z.infer<typeof RotationHistoryEntrySchema>;

export const DeviceCredentialsDataSchema = z.object({
  device_id: z.string(),
  registered_device_id: z.string(),
  device_name: z.string(),
  device_type: z.string(),
  mac_address: z.string().nullable(),
  has_credentials: z.boolean(),
  is_masked: z.boolean(),
  device_secret: z.string(),
  device_signature_key: z.string(),
  secret_rotated_at: z.string(),
  rotation_count: z.number(),
  telemetry_documentation: TelemetryDocumentationSchema.nullable(),
  rotation_history: z.array(RotationHistoryEntrySchema).default([]),
});
export type DeviceCredentialsData = z.infer<typeof DeviceCredentialsDataSchema>;

export const DisplayModuleTelemetryLogSchema = z.object({
  id: z.string(),
  device_id: z.string(),
  assignment_id: z.string(),
  client_id: z.string(),
  wifi_strength: WifiStrengthSchema,
  cpu_load: z.number(),
  memory_load_percentage: z.number(),
  memory_available: z.number(),
  memory_total: z.number(),
  connected_wifi_name: z.string(),
  resolved_system_wifi_id: z.string().nullable(),
  wifi_rssi: z.string(),
  mac_id: z.string(),
  os_hash: z.string(),
  received_timestamp: z.string(),
  request_id: z.string(),
});
export type DisplayModuleTelemetryLog = z.infer<typeof DisplayModuleTelemetryLogSchema>;

export const DeviceTelemetryDashboardDataSchema = z.object({
  device: z.object({
    id: z.string(),
    device_name: z.string(),
    registered_device_id: z.string(),
    mac_address: z.string().nullable(),
    device_type: z.string(),
    status: z.string(),
    os_name: z.string().nullable(),
    os_version: z.string().nullable(),
    expected_os_hash: z.string().nullable(),
    last_seen_at: z.string().nullable(),
  }),
  assignment: z
    .object({
      assignment_id: z.string(),
      client_id: z.string(),
      client_name: z.string(),
      configured_wifi_ssid: z.string().nullable(),
    })
    .nullable(),
  retention_limit: z.number(),
  total_retained: z.number(),
  latest_telemetry: DisplayModuleTelemetryLogSchema.nullable(),
  logs: z.array(DisplayModuleTelemetryLogSchema).max(RETENTION_LIMIT),
});
export type DeviceTelemetryDashboardData = z.infer<typeof DeviceTelemetryDashboardDataSchema>;
